<script setup lang="ts">
  import { computed, inject, onMounted, ref, type Ref } from 'vue'
  import { type ClientCase } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import clientPortalItems from '@/components/clientPortal/clientPortalItemsV3.vue'

  const caseRecord = inject<Ref<ClientCase | null>>('caseRecord')
  const { t } = useTranslation({
    en: {
      requiredActions: 'Required Actions',
      toProceedWithRequest:
        'to proceed with your request, please complete the following documents or questionnaires.'
    },
    de: {
      requiredActions: 'Handlung Erforderlich',
      toProceedWithRequest:
        'um mit Ihrer Anfrage fortzufahren, füllen Sie bitte die folgenden Dokumente oder Fragebögen aus.'
    }
  })

  const greeting = computed(() => {
    const caseWithClientNames = caseRecord?.value as any
    if (caseWithClientNames?.client_first_name && caseWithClientNames?.client_last_name) {
      return `Hallo ${caseWithClientNames.client_first_name} ${caseWithClientNames.client_last_name},`
    } else if (caseWithClientNames?.client_first_name) {
      return `Hallo ${caseWithClientNames.client_first_name},`
    } else if (caseWithClientNames?.client_last_name) {
      return `Hallo ${caseWithClientNames.client_last_name},`
    }
    return 'Hallo,'
  })

  const allComplete = ref(true)
  const checkAllComplete = () => {
    if (caseRecord?.value?.questionnaire_submissions) {
      for (const submission of caseRecord.value.questionnaire_submissions) {
        if (submission.state !== 'CLOSED') {
          allComplete.value = false
          return
        }
      }
    }

    // Check all form requests if all submissions are complete
    if (allComplete.value && caseRecord?.value?.form_requests) {
      for (const request of caseRecord.value.form_requests) {
        if (!request.response_present) {
          allComplete.value = false
          return
        }
      }
    }
  }

  onMounted(() => {
    checkAllComplete()
  })
</script>

<template>
  <div
    v-if="caseRecord"
    class="flex min-h-full flex-col bg-gray-50"
  >
    <!-- Header -->
    <div class="px-6 py-6">
      <div class="space-y-2">
        <h1 class="text-xl font-semibold text-black">
          {{ t('requiredActions') }}
        </h1>
        <p class="text-base leading-relaxed text-gray-700">
          {{ greeting }} {{ t('toProceedWithRequest') }}
        </p>
      </div>
    </div>
    <div class="flex-1 p-6">
      <clientPortalItems />
    </div>
  </div>
</template>
